import React, { useEffect, useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Eye, EyeSlash, ArrowLeft, Buildings } from "@phosphor-icons/react";
import { formatApiError } from "@/lib/api";
import api from "@/lib/api";
import { toast } from "sonner";
import DigitalSetuLogo from "@/components/DigitalSetuLogo";
import { BRAND } from "@/lib/brand";

function getDeviceId() {
  let id = localStorage.getItem("ds_device_id");
  if (!id) {
    id = (window.crypto?.randomUUID?.() || `dev-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`);
    localStorage.setItem("ds_device_id", id);
  }
  return id;
}

export default function Login() {
  const { user, login, pinLogin } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("admin@csc.local");
  const [password, setPassword] = useState("admin123");
  const [branch, setBranch] = useState("");
  const [remember, setRemember] = useState(true);
  const [branches, setBranches] = useState([]);
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  // Quick PIN login
  const [mode, setMode] = useState("password");       // password | pin
  const [pinEmail, setPinEmail] = useState("");
  const [pin, setPin] = useState("");
  const [showPinSetup, setShowPinSetup] = useState(false);
  const [setupPin, setSetupPin] = useState("");
  const [setupPin2, setSetupPin2] = useState("");
  const deviceId = getDeviceId();

  useEffect(() => {
    api.get("/public/branches").then((r) => setBranches(r.data || [])).catch(() => {});
    const savedEmail = localStorage.getItem("ds_pin_email") || "";
    if (savedEmail) {
      api.get("/auth/pin/status", { params: { email: savedEmail, device_id: deviceId } })
        .then((r) => {
          if (r.data.enabled && r.data.available && !r.data.locked) { setPinEmail(savedEmail); setMode("pin"); }
        })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (user && !showPinSetup) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr("");
    try {
      await login(email, password, { branch: branch === "_all" ? "" : branch, remember });
      toast.success("Signed in");
      // Offer Quick PIN setup on first successful password login (per device)
      try {
        const { data: st } = await api.get("/auth/pin/status", { params: { email, device_id: deviceId } });
        const skipped = localStorage.getItem(`ds_pin_skip_${email.toLowerCase()}`);
        if (st.enabled && !st.available && !skipped) { setShowPinSetup(true); return; }
      } catch { /* ignore */ }
      nav("/dashboard");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const submitPin = async (e) => {
    e.preventDefault();
    if (!/^\d{4}$|^\d{6}$/.test(pin)) { setErr("Enter your 4 or 6 digit PIN"); return; }
    setLoading(true); setErr("");
    try {
      await pinLogin(pinEmail, pin, deviceId);
      toast.success("Signed in with PIN");
      nav("/dashboard");
    } catch (e) {
      const status = e.response?.status;
      setErr(formatApiError(e.response?.data?.detail) || e.message);
      setPin("");
      if (status === 423 || status === 404 || status === 403) setMode("password");
    } finally { setLoading(false); }
  };

  const savePin = async () => {
    if (!/^\d{4}$|^\d{6}$/.test(setupPin)) { toast.error("PIN must be exactly 4 or 6 digits"); return; }
    if (setupPin !== setupPin2) { toast.error("PINs do not match"); return; }
    try {
      await api.post("/auth/pin/setup", { current_password: password, pin: setupPin, device_id: deviceId });
      localStorage.setItem("ds_pin_email", email.toLowerCase());
      toast.success("Quick PIN saved — next time just enter your PIN");
      setShowPinSetup(false);
      nav("/dashboard");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const skipPin = () => {
    localStorage.setItem(`ds_pin_skip_${email.toLowerCase()}`, "1");
    setShowPinSetup(false);
    nav("/dashboard");
  };

  return (
    <div className="min-h-screen flex">
      {/* Left: brand panel */}
      <div className="hidden lg:flex w-1/2 relative bg-gradient-to-br from-[#1e2f6b] via-[#1e3a8a] to-[#2e5fdf] text-white p-12 flex-col justify-between overflow-hidden">
        <div className="absolute -top-20 -right-20 w-96 h-96 bg-orange-500/25 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-20 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl" />

        <div className="relative z-10 flex items-center gap-3">
          <div className="w-11 h-11 rounded-md bg-white/10 flex items-center justify-center">
            <DigitalSetuLogo size={30} variant="mono" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-blue-200">{BRAND.sub}</div>
            <div className="text-2xl font-semibold" style={{ fontFamily: "var(--font-heading)" }}>{BRAND.name}</div>
          </div>
        </div>

        <div className="relative z-10 max-w-md">
          <div className="text-xs uppercase tracking-[0.2em] text-orange-300 mb-4">Staff Console · Multi-Branch</div>
          <h1 className="text-4xl font-bold mb-4 leading-tight" style={{ fontFamily: "var(--font-heading)" }}>
            One platform for services, billing, documents and AI drafting.
          </h1>
          <p className="text-blue-100 leading-relaxed">
            {BRAND.tagline}. Complete CSC · Aaple Sarkar · MahaOnline · Billing · POS · Document Management · AI Assistant.
          </p>
        </div>
        <div className="relative z-10 flex items-center justify-between text-xs text-blue-200">
          <span>© {new Date().getFullYear()} {BRAND.name}</span>
          <Link to="/" className="inline-flex items-center gap-1.5 text-blue-200 hover:text-white transition-colors" data-testid="back-to-site">
            <ArrowLeft size={12} /> Back to public site
          </Link>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12 grid-pattern">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8 flex items-center gap-2">
            <div className="w-9 h-9 rounded-md bg-[#1e3a8a] flex items-center justify-center">
              <DigitalSetuLogo size={24} variant="mono" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 -mb-0.5">{BRAND.sub}</div>
              <div className="font-semibold" style={{ fontFamily: "var(--font-heading)" }}>{BRAND.name}</div>
            </div>
          </div>

          {mode === "pin" && !showPinSetup ? (
            <>
              <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">Quick PIN Sign in</div>
              <h2 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: "var(--font-heading)" }}>Welcome back</h2>
              <p className="text-sm text-slate-600 mb-8">Enter your PIN for <span className="font-semibold text-slate-800">{pinEmail}</span> on this device.</p>
              <form onSubmit={submitPin} className="space-y-5" data-testid="pin-login-form">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-600">PIN</Label>
                  <Input
                    data-testid="pin-login-input"
                    type="password"
                    inputMode="numeric"
                    autoFocus
                    maxLength={6}
                    value={pin}
                    onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
                    className="h-14 text-center text-3xl tracking-[0.6em] font-bold focus-visible:ring-[#1e3a8a]/30 focus-visible:border-[#1e3a8a]"
                    placeholder="••••"
                  />
                </div>
                {err && <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md" data-testid="pin-login-error">{err}</div>}
                <Button type="submit" disabled={loading} data-testid="pin-login-btn"
                  className="w-full h-11 bg-[#1e3a8a] hover:bg-[#1e2f6b] text-white font-medium">
                  {loading ? "Signing in..." : "Login with PIN"}
                </Button>
                <button type="button" onClick={() => { setMode("password"); setErr(""); }} data-testid="use-password-instead-btn"
                  className="w-full text-sm text-[#1e3a8a] font-semibold hover:underline">
                  Use Password Instead
                </button>
              </form>
            </>
          ) : showPinSetup ? (
            <>
              <div className="text-xs uppercase tracking-[0.2em] text-emerald-600 mb-2">Optional · One-time setup</div>
              <h2 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: "var(--font-heading)" }}>Create Your Quick PIN</h2>
              <p className="text-sm text-slate-600 mb-8">Next time, sign in on this device with just a 4 or 6 digit PIN. Your password is never saved.</p>
              <div className="space-y-5" data-testid="pin-setup-form">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-600">PIN (4 or 6 digits)</Label>
                  <Input data-testid="pin-setup-input" type="password" inputMode="numeric" autoFocus maxLength={6}
                    value={setupPin} onChange={(e) => setSetupPin(e.target.value.replace(/\D/g, ""))}
                    className="h-14 text-center text-3xl tracking-[0.6em] font-bold" placeholder="••••" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-600">Confirm PIN</Label>
                  <Input data-testid="pin-setup-confirm-input" type="password" inputMode="numeric" maxLength={6}
                    value={setupPin2} onChange={(e) => setSetupPin2(e.target.value.replace(/\D/g, ""))}
                    className="h-14 text-center text-3xl tracking-[0.6em] font-bold" placeholder="••••" />
                </div>
                <Button onClick={savePin} data-testid="pin-setup-save-btn"
                  className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-medium">Save PIN</Button>
                <Button variant="outline" onClick={skipPin} data-testid="pin-setup-skip-btn" className="w-full h-11">Skip</Button>
                <div className="text-[11px] text-slate-500">PIN works only on this device · locks after 5 wrong attempts · resets if your password changes.</div>
              </div>
            </>
          ) : (
          <>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">Staff Sign in</div>
          <h2 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: "var(--font-heading)" }}>Welcome back</h2>
          <p className="text-sm text-slate-600 mb-8">Enter your staff credentials to access the ERP.</p>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-600">Email</Label>
              <Input id="email" data-testid="login-email-input" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-11 focus-visible:ring-[#1e3a8a]/30 focus-visible:border-[#1e3a8a]" required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-600">Password</Label>
              <div className="relative">
                <Input id="password" data-testid="login-password-input" type={showPw ? "text" : "password"} value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-11 pr-11 focus-visible:ring-[#1e3a8a]/30 focus-visible:border-[#1e3a8a]" required />
                <button type="button" onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-900"
                  data-testid="login-show-password-btn">
                  {showPw ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {err && <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md" data-testid="login-error">{err}</div>}

            <div className="space-y-2">
              <Label htmlFor="branch" className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-600">Branch (optional)</Label>
              <Select value={branch} onValueChange={setBranch}>
                <SelectTrigger id="branch" className="h-11" data-testid="login-branch-select">
                  <div className="flex items-center gap-2">
                    <Buildings size={14} className="text-slate-400" />
                    <SelectValue placeholder="🌐 All Branches (auto)" />
                  </div>
                </SelectTrigger>
                <SelectContent className="bg-white max-h-72">
                  <SelectItem value="_all">🌐 All Branches (auto-detect)</SelectItem>
                  {branches.map((b) => (
                    <SelectItem key={b.id} value={b.name}>
                      {b.is_head_office ? "🏢 " : "🏬 "} {b.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-[11px] text-slate-500">Super Admin &amp; Head Office users can leave this on Auto.</div>
            </div>

            <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer select-none" data-testid="login-remember">
              <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="w-4 h-4 accent-orange-600" />
              Keep me signed in for 30 days
            </label>

            <Button type="submit" disabled={loading} data-testid="login-submit-btn"
              className="w-full h-11 bg-[#1e3a8a] hover:bg-[#1e2f6b] text-white font-medium hover:-translate-y-px hover:shadow-md transition-[transform,box-shadow,background-color] duration-200">
              {loading ? "Signing in..." : "Sign in"}
            </Button>

            <div className="text-xs text-slate-500 pt-2 border-t border-slate-200">
              Default: <span className="font-mono text-slate-700">admin@csc.local</span> / <span className="font-mono text-slate-700">admin123</span>
            </div>
            <Link to="/" className="lg:hidden inline-flex items-center gap-1 text-xs text-[#1e3a8a] font-semibold" data-testid="back-to-site-mobile">
              <ArrowLeft size={11} /> Back to DigitalSetu site
            </Link>
          </form>
          </>
          )}
        </div>
      </div>
    </div>
  );
}
